import { useEffect, useState } from 'react'
import {
  PlusIcon,
  TrashIcon,
  BoltIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, InlineSpinner, EmptyState, Button, Modal, ModalBody, ModalFooter, Badge } from '@/components/ui'
import { servicesApi } from '@/api'
import type { ExpertSpecialization } from '@/api/services'
import toast from 'react-hot-toast'

interface CategoryRouting {
  id: number
  name: string
  slug: string
  questions_count: number
  pending_count: number
  experts_count: number
  available_experts_count: number
}

interface AvailableExpert {
  id: number
  username: string
  email: string
  full_name: string
  role: string
  job_title: string
}

export default function AdminClinicRoutingPage() {
  const [categories, setCategories] = useState<CategoryRouting[]>([])
  const [specializations, setSpecializations] = useState<ExpertSpecialization[]>([])
  const [availableExperts, setAvailableExperts] = useState<AvailableExpert[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<CategoryRouting | null>(null)

  // Add Specialization Modal
  const [showAddModal, setShowAddModal] = useState(false)
  const [addForm, setAddForm] = useState({
    expert: 0,
    clinic_category: 0,
    expertise_level: 'mid',
    max_assignments: 10,
    priority: 0,
  })
  const [saving, setSaving] = useState(false)

  // Stats
  const [stats, setStats] = useState<{
    total_assignments: number
    auto_assignments: number
    manual_assignments: number
    clinic_assignments: number
    expertise_assignments: number
    avg_response_time_hours: number | null
  } | null>(null)

  // Auto-assign state
  const [autoAssigning, setAutoAssigning] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [categoriesData, specializationsData, expertsData, statsData] = await Promise.all([
        servicesApi.getClinicCategoriesRouting(),
        servicesApi.getExpertSpecializations(),
        servicesApi.getAvailableExperts(),
        servicesApi.getAssignmentStats(),
      ])
      setCategories(categoriesData)
      setSpecializations(specializationsData)
      setAvailableExperts(expertsData)
      setStats(statsData)
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Errore nel caricamento dei dati')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenAddModal = (category: CategoryRouting) => {
    setSelectedCategory(category)
    setAddForm({
      expert: 0,
      clinic_category: category.id,
      expertise_level: 'mid',
      max_assignments: 10,
      priority: 0,
    })
    setShowAddModal(true)
  }

  const handleSaveSpecialization = async () => {
    if (!addForm.expert || !addForm.clinic_category) {
      toast.error('Seleziona un esperto')
      return
    }
    setSaving(true)
    try {
      await servicesApi.createExpertSpecialization({
        expert: addForm.expert,
        clinic_category: addForm.clinic_category,
        expertise_level: addForm.expertise_level,
        max_assignments: addForm.max_assignments,
        is_available: true,
        priority: addForm.priority,
      })
      toast.success('Specializzazione aggiunta')
      setShowAddModal(false)
      fetchData()
    } catch (error: unknown) {
      console.error('Error saving:', error)
      const err = error as { response?: { data?: { detail?: string } } }
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteSpecialization = async (id: number) => {
    if (!confirm('Sei sicuro di voler rimuovere questa specializzazione?')) return
    try {
      await servicesApi.deleteExpertSpecialization(id)
      toast.success('Specializzazione rimossa')
      fetchData()
    } catch (error) {
      console.error('Error deleting:', error)
      toast.error('Errore nella rimozione')
    }
  }

  const handleToggleAvailability = async (spec: ExpertSpecialization) => {
    try {
      await servicesApi.updateExpertSpecialization(spec.id, {
        is_available: !spec.is_available,
      })
      toast.success(spec.is_available ? 'Esperto disattivato' : 'Esperto attivato')
      fetchData()
    } catch (error) {
      console.error('Error toggling:', error)
      toast.error('Errore nell\'aggiornamento')
    }
  }

  const handleAutoAssignCategory = async (category: CategoryRouting) => {
    setAutoAssigning(true)
    try {
      const result = await servicesApi.autoAssignCategoryPending(category.slug)
      if (result.assigned > 0) {
        toast.success(`${result.assigned} domande assegnate automaticamente`)
      } else if (result.failed > 0) {
        toast(`Nessuna domanda assegnata. ${result.failed} fallite.`, { icon: '⚠️' })
      } else {
        toast('Nessuna domanda in attesa da assegnare', { icon: 'ℹ️' })
      }
      fetchData()
    } catch (error) {
      console.error('Error auto-assigning:', error)
      toast.error('Errore nell\'assegnazione automatica')
    } finally {
      setAutoAssigning(false)
    }
  }

  const handleBulkAutoAssign = async () => {
    setAutoAssigning(true)
    try {
      const result = await servicesApi.bulkAutoAssign()
      toast.success(result.detail)
      fetchData()
    } catch (error) {
      console.error('Error bulk auto-assigning:', error)
      toast.error('Errore nell\'assegnazione automatica')
    } finally {
      setAutoAssigning(false)
    }
  }

  const getLevelBadgeColor = (level: string) => {
    switch (level) {
      case 'lead': return 'bg-purple-100 text-purple-700'
      case 'senior': return 'bg-blue-100 text-blue-700'
      case 'mid': return 'bg-green-100 text-green-700'
      case 'junior': return 'bg-gray-100 text-gray-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  const getLevelLabel = (level: string) => {
    switch (level) {
      case 'lead': return 'Lead Expert'
      case 'senior': return 'Senior'
      case 'mid': return 'Intermedio'
      case 'junior': return 'Junior'
      default: return level
    }
  }

  if (loading) {
    return <InlineSpinner message="Caricamento routing esperti..." />
  }

  const totalPending = categories.reduce((sum, cat) => sum + cat.pending_count, 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Smistamento Automatico</h1>
          <p className="text-gray-600">Gestisci l'assegnazione automatica delle domande agli esperti</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={fetchData}>
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Aggiorna
          </Button>
          <Button onClick={handleBulkAutoAssign} disabled={autoAssigning || totalPending === 0}>
            <BoltIcon className="h-5 w-5 mr-2" />
            {autoAssigning ? 'Assegnazione...' : `Assegna Tutte (${totalPending})`}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ChartBarIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Assegnazioni Totali</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_assignments}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <BoltIcon className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Automatiche</p>
                <p className="text-2xl font-bold text-green-600">{stats.auto_assignments}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <UserGroupIcon className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Manuali</p>
                <p className="text-2xl font-bold text-orange-600">{stats.manual_assignments}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ClockIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Tempo Medio Risposta</p>
                <p className="text-2xl font-bold text-purple-600">
                  {stats.avg_response_time_hours ? `${stats.avg_response_time_hours}h` : '-'}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Categories with Experts */}
      <div className="space-y-6">
        {categories.length > 0 ? (
          categories.map((category) => {
            const categorySpecs = specializations.filter(s => s.clinic_category === category.id)
            const availableSpecs = categorySpecs.filter(s => s.is_available && !s.is_at_capacity)

            return (
              <Card key={category.id} className="overflow-hidden">
                <CardHeader
                  title={category.name}
                  subtitle={`${category.questions_count} domande totali`}
                  action={
                    <div className="flex items-center gap-3">
                      {category.pending_count > 0 && (
                        <Badge variant="warning">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {category.pending_count} in attesa
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleAutoAssignCategory(category)}
                        disabled={autoAssigning || category.pending_count === 0}
                      >
                        <BoltIcon className="h-4 w-4 mr-1" />
                        Auto-Assegna
                      </Button>
                      <Button size="sm" onClick={() => handleOpenAddModal(category)}>
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Aggiungi Esperto
                      </Button>
                    </div>
                  }
                />

                {/* Status Bar */}
                <div className="px-4 py-2 bg-gray-50 border-b flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <UserGroupIcon className="h-4 w-4 text-gray-500" />
                    <span className="text-gray-600">{categorySpecs.length} esperti configurati</span>
                  </span>
                  {availableSpecs.length > 0 ? (
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircleIcon className="h-4 w-4" />
                      {availableSpecs.length} disponibili
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-amber-600">
                      <ExclamationTriangleIcon className="h-4 w-4" />
                      Nessun esperto disponibile
                    </span>
                  )}
                </div>

                {/* Experts Table */}
                {categorySpecs.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Esperto</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Livello</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Carico</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priorità</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {categorySpecs.map((spec) => (
                          <tr key={spec.id} className={`hover:bg-gray-50 ${!spec.is_available ? 'opacity-60' : ''}`}>
                            <td className="px-4 py-4">
                              <div>
                                <p className="font-medium text-gray-900">{spec.expert_name}</p>
                                <p className="text-sm text-gray-500">{spec.expert_email}</p>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelBadgeColor(spec.expertise_level)}`}>
                                {getLevelLabel(spec.expertise_level)}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${
                                      spec.is_at_capacity ? 'bg-red-500' : 'bg-green-500'
                                    }`}
                                    style={{ width: `${Math.min((spec.current_assignments / spec.max_assignments) * 100, 100)}%` }}
                                  />
                                </div>
                                <span className="text-sm text-gray-600">
                                  {spec.current_assignments}/{spec.max_assignments}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">
                              {spec.priority}
                            </td>
                            <td className="px-4 py-4">
                              {spec.is_at_capacity ? (
                                <Badge variant="danger" size="sm">Pieno</Badge>
                              ) : spec.is_available ? (
                                <Badge variant="success" size="sm">Disponibile</Badge>
                              ) : (
                                <Badge variant="default" size="sm">Non attivo</Badge>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleToggleAvailability(spec)}
                                  className={`px-2 py-1 text-xs rounded ${
                                    spec.is_available
                                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                                  }`}
                                >
                                  {spec.is_available ? 'Disattiva' : 'Attiva'}
                                </button>
                                <button
                                  onClick={() => handleDeleteSpecialization(spec.id)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                                  title="Rimuovi"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <UserGroupIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500 mb-3">Nessun esperto configurato per questa categoria</p>
                    <Button size="sm" onClick={() => handleOpenAddModal(category)}>
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Aggiungi Esperto
                    </Button>
                  </div>
                )}
              </Card>
            )
          })
        ) : (
          <Card>
            <EmptyState
              icon={<UserGroupIcon className="h-12 w-12" />}
              title="Nessuna categoria"
              description="Crea prima le categorie nella sezione Compliance Clinic"
            />
          </Card>
        )}
      </div>

      {/* Add Specialization Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={`Aggiungi Esperto - ${selectedCategory?.name}`}
      >
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Esperto *</label>
              <select
                value={addForm.expert}
                onChange={(e) => setAddForm({ ...addForm, expert: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value={0}>Seleziona un esperto...</option>
                {availableExperts
                  .filter(e => !specializations.some(s =>
                    s.expert === e.id && s.clinic_category === selectedCategory?.id
                  ))
                  .map((expert) => (
                    <option key={expert.id} value={expert.id}>
                      {expert.full_name} ({expert.role})
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Livello Expertise</label>
              <select
                value={addForm.expertise_level}
                onChange={(e) => setAddForm({ ...addForm, expertise_level: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="junior">Junior</option>
                <option value="mid">Intermedio</option>
                <option value="senior">Senior</option>
                <option value="lead">Lead Expert</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Assegnazioni Contemporanee
              </label>
              <input
                type="number"
                value={addForm.max_assignments}
                onChange={(e) => setAddForm({ ...addForm, max_assignments: parseInt(e.target.value) || 10 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                min={1}
                max={100}
              />
              <p className="mt-1 text-xs text-gray-500">
                Numero massimo di domande che possono essere assegnate a questo esperto contemporaneamente
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priorità</label>
              <input
                type="number"
                value={addForm.priority}
                onChange={(e) => setAddForm({ ...addForm, priority: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                min={0}
              />
              <p className="mt-1 text-xs text-gray-500">
                0 = massima priorità. Gli esperti con priorità inferiore ricevono domande per primi.
              </p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowAddModal(false)}>Annulla</Button>
          <Button onClick={handleSaveSpecialization} disabled={saving || !addForm.expert}>
            {saving ? 'Salvataggio...' : 'Aggiungi Esperto'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
